import { ValuationApproachFutureExit } from 'api';
import { FUTURE_EQUITY_SPREADSHEET_TABLE_NAMES } from 'pages/ValuationsAllocation/common/constants/futureExit/sheetConfigs';
import { getApproachTableName } from 'pages/ValuationsAllocation/util';
import { getNumberValue, getObjectValue, getStringValue } from 'utilities';
import { createAllocationMethodOPMConfig, createAllocationMethodWaterfallCSEConfig } from './AllocationMethodTable';
import { createFutureEquityConfiguration } from './FutureEquity/config';
import FutureExit from './FutureExitApproach';
import { createModifiedPresentEquityConfiguration } from './ModifiedPresentEquity/config';
import { CreateFutureExitConfigParams, SecurityWithValue } from './types';
import createValuesPerShareSpreadsheets from './ValuesPerShareBase/createValuesPerShareSpreadsheets';

const createFutureExitConfig = (params: CreateFutureExitConfigParams) => {
  const {
    approach,
    approaches,
    company,
    companyExchangeRate,
    financials,
    futureExitAttributes,
    isDisabled,
    measurementDate,
    futureExitRiskFreeRate,
    capTableList,
  } = params;
  const { valuations_approach_future_exit: valuationsApproachFutureExit } = getObjectValue(approach);
  const ValuationsApproachFutureExit = valuationsApproachFutureExit as ValuationApproachFutureExit;
  const name = getApproachTableName({ approach });
  const { futureExitApproachAttrs } = futureExitAttributes;

  // FUTURE EQUITY VALUE - TABLE
  const futureEquitySheet = createFutureEquityConfiguration({
    approach,
    approaches,
    isDisabled,
    financials,
    measurementDate,
    fieldAttributes: futureExitAttributes,
  });

  const { total_cash_equivalents: totalCashEquivalents, total_debt: totalDebt } = getObjectValue(financials);

  const { captable_currency: captableCurrency, financials_currency: financialsCurrency } = getObjectValue(company);

  // Handling and adjusting cells if the Company has different currencies
  const isUniformCurrency = captableCurrency === financialsCurrency;

  // If is not uniform currency, multiply the values by the Company exchange rate
  const plusDebtTotal = isUniformCurrency ? totalDebt : totalDebt * getNumberValue(companyExchangeRate ?? 1);
  const lessCashTotal = isUniformCurrency
    ? totalCashEquivalents
    : totalCashEquivalents * getNumberValue(companyExchangeRate ?? 1);

  // MODIFIED PRESENT EQUITY VALUE - TABLE
  const presentValueSheet = createModifiedPresentEquityConfiguration({
    approach,
    approaches,
    futureEquitySheet,
    futureExitApproachAttrs,
    isDisabled,
    lessCashTotal,
    ltmData: financials,
    measurementDate,
    plusDebtTotal,
  });

  const allocationMethodWaterfallCSEName = getApproachTableName({
    approach,
    tableSuffix: FUTURE_EQUITY_SPREADSHEET_TABLE_NAMES.ALLOCATION_METHOD_VALUE_WATERFALL_CSE,
  });

  // ALLOCATION METHOD CSE - TABLE
  const allocationMethodWaterfallCSESheet = createAllocationMethodWaterfallCSEConfig({
    approach,
    approaches,
    valuationsApproachFutureExit: ValuationsApproachFutureExit,
    name: allocationMethodWaterfallCSEName,
    measurementDate,
    isDisabled,
    futureExitApproachAttrs,
  });
  const allocationMethodOPMName = getApproachTableName({
    approach,
    tableSuffix: FUTURE_EQUITY_SPREADSHEET_TABLE_NAMES.ALLOCATION_METHOD_VALUE_OPM,
  });

  // ALLOCATION METHOD OPM - TABLE
  const allocationMethodOPMSheet = createAllocationMethodOPMConfig({
    approach,
    approaches,
    valuationsApproachFutureExit: ValuationsApproachFutureExit,
    name: allocationMethodOPMName,
    measurementDate,
    isDisabled,
    futureExitRiskFreeRate,
    futureExitApproachAttrs,
  });

  // VALUES PER SHARE SPREADSHEETS
  const { present_share_values: presentShareValues, future_share_values: futureShareValues } = getObjectValue(ValuationsApproachFutureExit);
  
  // Create a map of security ID to security name from cap table data
  const createSecurityNameMap = (): Map<number, string> => {
    const securityMap = new Map<number, string>();
    
    if (capTableList) {
      capTableList.forEach(capTable => {
        if (capTable.securities) {
          capTable.securities.forEach(security => {
            securityMap.set(security.security, security.name);
          });
        }
      });
    }
    
    return securityMap;
  };
  
  const securityNameMap = createSecurityNameMap();
  
  // Transform API data to SecurityWithValue format
  const getValuesPerShare = (dataShareValues?: unknown[]): SecurityWithValue[] => {
    if (!dataShareValues) {
      return [];
    }
    return dataShareValues.map((item: unknown) => {
      const typedItem = item as { security: number; security_name?: string; value?: number };
      const securityId = typedItem.security;
      
      // Try to get security name from cap table first, fallback to API data
      const securityName = securityNameMap.get(securityId) || getStringValue(typedItem.security_name);
      
      return {
        security: {
          id: securityId,
          name: securityName,
        },
        value: getNumberValue(typedItem.value),
      };
    });
  };

  const valuesPerShareSpreadsheets = createValuesPerShareSpreadsheets({
    approach,
    futureValuesPerShare: getValuesPerShare(futureShareValues),
    presentValuesPerShare: getValuesPerShare(presentShareValues),
  });

  return {
    name,
    approach,
    value: approach?.enterprise_value,
    spreadsheets: {
      futureEquitySheet,
      presentValueSheet,
      allocationMethodWaterfallCSESheet,
      allocationMethodOPMSheet,
      // Include spreadsheets using their actual names as keys
      ...(valuesPerShareSpreadsheets.presentValuesPerShareSheet?.name && {
        [valuesPerShareSpreadsheets.presentValuesPerShareSheet.name]: valuesPerShareSpreadsheets.presentValuesPerShareSheet,
      }),
      ...(valuesPerShareSpreadsheets.futureValuesPerShareSheet?.name && {
        [valuesPerShareSpreadsheets.futureValuesPerShareSheet.name]: valuesPerShareSpreadsheets.futureValuesPerShareSheet,
      }),
    },
    enterpriseValueReference: presentValueSheet?.name,
    measurementDate,
    component: FutureExit,
  };
};

export default createFutureExitConfig;
